/**
 * The cover document's state for one gathering — the wiring ClubPage keeps inline, lifted so the
 * public page, the editor and the manager share it: photos in order with framing, layout, split,
 * count, card; measured photo shapes; drag-to-reframe with one debounced PUT; the API adapter.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useImageRatios from '../../hooks/useImageRatios';
import gatheringService from '../../services/gatheringService';
import { resolveLayout } from '../event/coverLayout';
import { coverFromGathering, coverSlice, gatheringCoverApi } from './gatheringCoverModel';

export default function useGatheringCover(gathering) {
  const [cover, setCover] = useState(() => coverFromGathering(gathering));
  const [coverFor, setCoverFor] = useState(gathering);
  // Re-derived during render (not in an effect) so the first paint already has the photos.
  if (gathering !== coverFor) { setCoverFor(gathering); setCover(coverFromGathering(gathering)); }
  const ratios = useImageRatios(cover.photos.map((p) => p.src));
  const shapedCover = useMemo(() => ({ ...cover, photos: cover.photos.map((p) => ({ ...p, r: ratios[p.src] || p.r, measured: !!ratios[p.src] })) }), [cover, ratios]);
  const coverPhotos = useMemo(() => coverSlice(shapedCover), [shapedCover]);
  const layoutKey = resolveLayout(shapedCover.layout, coverPhotos);
  const gatheringId = gathering?.id || null;
  const api = useMemo(() => (gatheringId ? gatheringCoverApi(gatheringId) : null), [gatheringId]);

  const focalTimer = useRef(null);
  const focalPending = useRef({});
  const idRef = useRef(null);
  idRef.current = gatheringId;
  const flushFocal = useCallback(() => {
    focalTimer.current = null;
    const focal = Object.values(focalPending.current);
    focalPending.current = {};
    if (idRef.current && focal.length) gatheringService.updateCover(idRef.current, { focal });
  }, []);
  // A drag followed at once by leaving the page used to lose its framing with the timer.
  useEffect(() => () => {
    if (focalTimer.current == null) return;
    window.clearTimeout(focalTimer.current);
    flushFocal();
  }, [flushFocal]);
  const onFocal = useCallback((pid, fx, fy) => {
    setCover((c) => ({ ...c, photos: c.photos.map((p) => (p.id === pid ? { ...p, fx, fy } : p)) }));
    focalPending.current[pid] = { id: pid, x: Math.round(fx), y: Math.round(fy) };
    window.clearTimeout(focalTimer.current);
    focalTimer.current = window.setTimeout(flushFocal, 600);
  }, [flushFocal]);

  return { cover, setCover, shapedCover, coverPhotos, layoutKey, onFocal, api };
}
